import taskManagerApi from '@/features/shared/services/taskManagerApi';
/**
 * Fetch assignments optionally filtered by any parameters.
 * @param filters - Optional filters (e.g. task_id, user_id, etc.)
 * @returns Promise resolving to assignment data.
 */
export async function fetchAssignments(filters) {
    try {
        const response = await taskManagerApi.get('/assignment', { params: filters });
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des assignations");
        }
    }
}
/**
 * Fetch a single assignment by ID.
 */
export async function fetchAssignmentById(id) {
    try {
        const response = await taskManagerApi.get(`/assignment/${id}`);
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error(`Erreur inconnue lors de la récupération de l'assignation ${id}`);
    }
}
/**
 * Create a new assignment.
 */
export async function createAssignment(assignmentData) {
    try {
        const response = await taskManagerApi.post('/assignment', assignmentData);
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error("Erreur inconnue lors de la création de l'assignation");
    }
}
/**
 * Update an existing assignment.
 */
export async function updateAssignment(id, assignmentData) {
    try {
        const response = await taskManagerApi.put(`/assignment/${id}`, assignmentData);
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error("Erreur inconnue lors de la mise à jour de l'assignation");
    }
}
/**
 * Delete an assignment by its ID.
 */
export async function deleteAssignment(id) {
    try {
        await taskManagerApi.delete(`/assignment/${id}`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error("Erreur inconnue lors de la suppression de l'assignation");
    }
}
/**
 * Close an assignment by its ID.
 */
export async function closeAssignment(id) {
    try {
        await taskManagerApi.post(`/assignment/${id}/close`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error("Erreur inconnue lors de la clôture de l'assignation");
    }
}
/**
 * Assign multiple users to a task.
 * If only one user is provided and that user is already assigned, throw an error.
 * If multiple users are provided, ignore those already assigned and assign the rest.
 *
 * @param taskId - The ID of the task.
 * @param userIds - Array of user IDs to assign.
 */
export async function createMultipleUsersAssignment(taskId, userIds) {
    try {
        const response = await taskManagerApi.post('/assignment/assign-multiple', { taskId, userIds });
        return response.data.status === 'SUCCESS';
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        throw new Error("Erreur inconnue lors de l'assignation multiple d'utilisateurs");
    }
}
/**
 * Fetch the list of users assigned to a task.
 * The new route is: /task/:task_id/assignees
 *
 * @param taskId - The ID of the task.
 * @returns A promise resolving to an array of User objects.
 */
export async function fetchTaskAssignees(taskId) {
    try {
        const response = await taskManagerApi.get(`assignment/task/${taskId}/assignees`);
        return response.data.data.assignees;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des assignations de la tâche");
        }
    }
}
